import {
  FETCH_BOOKINGS,
  FETCH_BOOKINGS_SUCCESS,
  FETCH_BOOKINGS_FAILED,
  UPDATE_BOOKING,
  CANCEL_BOOKING
} from "../store/types";
import { fetchBookingLocations, stopTrackedBookingLocation } from '../actions/locationactions';
import { RequestPushMsg } from '../other/NotificationFunctions';
import store from '../store/store';
import { firebase, MOBILE_BOOKINGS_LIMIT } from '../config/configureFirebase';
import { addActualsToBooking, fetchTripEndAddress, updateDriverQueue } from "../other/sharedFunctions";
import { isFlatRateDriver } from "../other/flatRate";
import { get, onValue, onChildAdded, onChildChanged, update, off, remove, push } from "firebase/database";
import { uploadBytesResumable, getDownloadURL } from "firebase/storage";
import base64 from 'react-native-base64';
import AccessKey from '../other/AccessKey';
import { createIDBCache } from '../other/idbCache';
import { patchUsersCacheUpsert } from './usersactions';

// IndexedDB cache for the admin one-shot bookings fetch. Drivers and
// customers use the live onValue flow and bypass this cache entirely
// (they need real-time updates). TTL is 10 min — long enough to make
// reload-spam cheap, short enough that an admin scanning the dashboard
// for fresh bookings won't be looking at hours-old data.
//
// Mutations from this admin (updateBooking / cancelBooking) surgically
// patch the affected booking in the cache instead of invalidating, so
// admin status changes are reflected on the next page load without
// triggering a full /bookings refetch.
const BOOKINGS_CACHE_TTL_MS = 10 * 60 * 1000;
const bookingsCache = createIDBCache('donkey_bookings_cache', 'bookings', BOOKINGS_CACHE_TTL_MS);

// Stable cache key for the default 30-day window — was previously per-uid
// + per-fromTs, but fromTs is recomputed on each call (and changes at
// midnight) so the key wasn't actually stable for patching. Single key
// works because all admin-scope roles see the same /bookings tree
// (admin/callcenter/finance/scout). Custom date ranges skip the cache
// and fetch fresh.
const BOOKINGS_CACHE_KEY = 'admin_default';
// Separate key while settings.useTripdateMsIndex is on: a flag flip must never
// hydrate a cache built from the other index next to a tail listener on this
// one for the 10-min TTL. Flag read mirrors useCustomerOnDemand (usersactions).
const BOOKINGS_CACHE_KEY_MS = 'admin_default_ms';
const tripdateMsIndexEnabled = () => {
  try { return store.getState().settingsdata.settings.useTripdateMsIndex === true; } catch (_) { return false; }
};
const bookingsCacheKey = () => (tripdateMsIndexEnabled() ? BOOKINGS_CACHE_KEY_MS : BOOKINGS_CACHE_KEY);
// PENDING_DRIVER_APPROVAL is the donkey-meter self-join holding state — the
// customer's already requested but the driver hasn't tapped ✓ yet. Treating
// it as active means it shows up in MyBooking and contributes to the tab
// badge, so the customer has a clear handle to get back to their pending
// request (route handler in RideListScreen sends them to JoinDonkeyMeter).
const ACTIVE_BOOKING_STATUSES = ['PAYMENT_PENDING','NEW','ACCEPTED','ARRIVED','STARTED','REACHED','PENDING','PAID','PENDING_DRIVER_APPROVAL'];

const invalidateBookingsCache = () => bookingsCache.invalidateAll();

// Re-read affected user records from RTDB after a delay so the cache
// reflects server-side mutations made by Cloud Functions (wallet credit,
// topup debit, earnings push, etc.) that fire AFTER the client action
// returns. Without this, an admin who marks a booking PAID/COMPLETE
// would see a stale walletBalance/topUpBalance for that driver until
// the next full /users refetch.
//
// Gated to browser environments so mobile (driver flipping status from
// the app) doesn't pay for an extra per-user get() — there's no cache
// to refresh on mobile anyway.
//
// 5s delay covers the typical Cloud Function settlement latency. If
// settlement is slower, the next admin page reload after TTL still
// catches up; this is best-effort optimization, not correctness-critical.
const refreshUsersAfterSettlement = (uids, delayMs = 5000) => {
  if (typeof window === 'undefined' || !window.indexedDB) return;
  if (!uids || uids.length === 0) return;
  const unique = Array.from(new Set(uids.filter(Boolean)));
  if (unique.length === 0) return;
  setTimeout(async () => {
    const { singleUserRef } = firebase;
    for (const uid of unique) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const snap = await get(singleUserRef(uid));
        const userData = snap.val();
        if (userData) {
          patchUsersCacheUpsert(uid, userData);
        }
      } catch (err) {
        console.warn('[bookings] refreshUsersAfterSettlement failed for', uid, err && err.message);
      }
    }
  }, delayMs);
};

// Module-level handles for the admin "live updates" tail listeners.
// One for new bookings (onChildAdded), one for status flips on existing
// recent bookings (onChildChanged). Tracks unsub functions so we can
// detach stale listeners before re-attaching on fresh dispatches
// (avoids accumulating listeners across reloads / role changes).
let _adminTailUnsub = null;
let _adminTailChangeUnsub = null;
// Last driver/customer/fleetadmin bookings query we attached onValue to.
// off(baseRef) below only clears listeners on the SAME query, so a role
// change (customer → driver in the onboarding wizard, SettingsScreen
// convert) or a same-device re-login used to leave the previous role's
// listener attached and still dispatching into the slice. Detach it first.
let _mobileBookingsQuery = null;

// Surgical cache patch for a booking update. Merges `bookingPatch` into
// the cached booking entry (matched by id), recomputes the active list
// based on the new status, and writes back. No-op if the booking isn't
// in the cache (e.g., older than the cached window).
const patchBookingInCache = async (bookingId, bookingPatch) => {
  if (!bookingId || !bookingPatch) return;
  try {
    await bookingsCache.patch(bookingsCacheKey(), (current) => {
      if (!current || !Array.isArray(current.bookings)) return null;
      const idx = current.bookings.findIndex(b => b.id === bookingId);
      if (idx < 0) return null;
      const updated = { ...current.bookings[idx], ...bookingPatch };
      const newBookings = [...current.bookings];
      newBookings[idx] = updated;
      return {
        ...current,
        bookings: newBookings,
        active: newBookings.filter(b => ACTIVE_BOOKING_STATUSES.includes(b.status)),
      };
    });
  } catch (_) { invalidateBookingsCache(); }
};


export const fetchBookings = (dateRange = null) => (dispatch) => {

  const {
    bookingListRef,
    bookingListByMsRef,
  } = firebase;

  dispatch({
    type: FETCH_BOOKINGS,
    payload: null,
  });

  const userInfo = store.getState().auth.profile;
  // Every RESULT below (SUCCESS or FAILED) is stamped with the uid:role it
  // was fetched for. AppNavigator's cold-start onboarding guard compares it
  // to the signed-in profile to know the list is CURRENT for this account —
  // remount-proof (unlike a counter baseline) and never fooled by a previous
  // account's list after a same-device re-login.
  const fetchedFor = userInfo && userInfo.uid ? userInfo.uid + ':' + (userInfo.usertype || '') : null;

  // Calculate default date range (last 30 days) if not provided
  let fromDate, toDate;
  if (dateRange) {
    fromDate = dateRange.fromDate;
    toDate = dateRange.toDate;
  } else {
    const now = new Date();
    // Add 1 day buffer to include bookings created today/in the future
    now.setDate(now.getDate() + 1);
    now.setHours(23, 59, 59, 999);
    toDate = now.toISOString();
    // Default look-back window, role-based to cut the per-session /bookings
    // download. Admin + finance keep the full window (only ~2 users, and they
    // need the broad view for oversight/reports). The bulk — call-center
    // agents/supervisors/scouts — default to a much shorter window; they can
    // pull older on demand via the BookingHistory "Time range" control (which
    // passes an explicit dateRange and bypasses this default). Both tunable via
    // settings without a redeploy.
    const settings = store.getState().settingsdata.settings || {};
    const FULL_WINDOW_ROLES = ['admin', 'finance'];
    const windowDays = FULL_WINDOW_ROLES.includes(userInfo.usertype)
      ? (settings.bookingWindowDaysAdmin || 30)
      : (settings.bookingWindowDaysCallCenter || 7);
    const from = new Date();
    from.setDate(from.getDate() - windowDays);
    from.setHours(0, 0, 0, 0);
    fromDate = from.toISOString();
  }

  // Admin-scope roles get the date floor enforced SERVER-side via
  // bookingListRef's tripdate query. Driver/customer/fleetadmin queries
  // are already narrowly scoped by uid (small payload), so we don't pass
  // fromTs to them — and they keep the original client-side date window
  // for backward compatibility with the mobile UI.
  const ADMIN_SCOPE_ROLES = ['admin', 'callcenteragent', 'callcentersupervisor', 'finance', 'scout'];
  const isAdminScope = ADMIN_SCOPE_ROLES.includes(userInfo.usertype);
  // settings.useTripdateMsIndex (default OFF = byte-identical): admin-scope
  // roles query the server-stamped numeric `tripdateMs` mirror instead of the
  // mixed-type `tripdate` index, whose numeric startAt silently pulls every
  // ISO-string Donkey Meter booking ever created. Mobile roles never satisfy
  // isAdminScope, so their scoped queries are untouched either way.
  const useMs = isAdminScope && tripdateMsIndexEnabled();
  const defaultCacheKey = useMs ? BOOKINGS_CACHE_KEY_MS : BOOKINGS_CACHE_KEY;
  const fromTs = new Date(fromDate).getTime();
  // Mobile roles (customer / driver) also pass MOBILE_BOOKINGS_LIMIT: the
  // live list listener is capped to the newest N bookings (push ids are
  // chronological) instead of replaying the lifetime history at every start
  // and reconnect; the 7-day client window below still applies on top.
  // fleetadmin ignores the argument (its branch in bookingListRef is untouched).
  const baseRef = useMs
    ? bookingListByMsRef(fromTs)
    : bookingListRef(userInfo.uid, userInfo.usertype, isAdminScope ? fromTs : undefined, isAdminScope ? undefined : MOBILE_BOOKINGS_LIMIT);

  // Admin-scope roles use a one-shot get() instead of a continuous onValue
  // listener. Their pages display historical data and refresh on demand —
  // they don't need every status flip from every driver in the system to
  // re-trigger a full re-download. This is the single biggest RTDB cost
  // reduction. Drivers/customers/fleetadmin keep onValue (their flow needs
  // real-time updates and their queries are already narrowly scoped).

  const processSnapshot = (snapshot) => {
    // One val() per delivery (each call builds a fresh object tree of up to 300
    // bookings) and the window bounds once, not per record (PERFORMANCE_AUDIT P2-1).
    const data = snapshot.val();
    if (data) {
      const active = [];
      let tracked = null;
      const fromDateObj = new Date(fromDate);
      const toDateObj = new Date(toDate);
      const fiveMinutesAgo = new Date();
      fiveMinutesAgo.setMinutes(fiveMinutesAgo.getMinutes() - 5);
      const bookings = Object.keys(data)
        .map((i) => {
          data[i].id = i;
          data[i].pickupAddress = data[i].pickup.add;
          data[i].dropAddress = data[i].drop.add;
          data[i].discount = data[i].discount
            ? data[i].discount
            : 0;
          data[i].cashPaymentAmount = data[i].cashPaymentAmount
            ? data[i].cashPaymentAmount
            : 0;
          data[i].cardPaymentAmount = data[i].cardPaymentAmount
            ? data[i].cardPaymentAmount
            : 0;
          // Map tripdate to bookingDate for display (handles both formats)
          data[i].bookingDate = data[i].tripdate || data[i].bookingDate;

          // Client-side date filter — REQUIRED for non-admin roles (their
          // server query doesn't filter by date). Admin roles already had
          // the lower bound enforced server-side, so the lower-bound check
          // here is a no-op for them but kept for safety.
          const bookingDateStr = data[i].tripdate || data[i].bookingDate;
          if (bookingDateStr) {
            const bookingDate = new Date(bookingDateStr);
            // Always include bookings created in the last 5 minutes (handles race conditions)
            const isRecentBooking = bookingDate >= fiveMinutesAgo;

            if (!isRecentBooking && (bookingDate < fromDateObj || bookingDate > toDateObj)) {
              return null;
            }
          }

          return data[i];
        }).filter(booking => booking !== null);
      for (let i = 0; i < bookings.length; i++) {
        // Shared ACTIVE_BOOKING_STATUSES — see comment on the constant.
        // Includes PENDING_DRIVER_APPROVAL so donkey-meter pending self-joins
        // surface in MyBooking and the tab badge increments.
        if (ACTIVE_BOOKING_STATUSES.indexOf(bookings[i].status) != -1) {
          active.push(bookings[i]);
        }
        if ((['ACCEPTED', 'ARRIVED', 'STARTED'].indexOf(bookings[i].status) != -1) && userInfo.usertype == 'driver') {
          tracked = bookings[i];
        }
      }
      // ONE /tracking listener per delivery, on the booking `tracked` ends up
      // pointing at (unchanged: the last in-progress one), instead of a call
      // per matching booking inside the loop. fetchBookingLocations also
      // dedupes by booking id across deliveries.
      if (tracked) {
        fetchBookingLocations(tracked.id)(dispatch);
      } else if (userInfo.usertype == 'driver') {
        // The driver's trip ended: the /tracking listener of the last tracked trip
        // stayed attached for the rest of the session (only the customer path
        // detached it; PERFORMANCE_AUDIT P2-2).
        stopTrackedBookingLocation()(dispatch);
      }
      dispatch({
        type: FETCH_BOOKINGS_SUCCESS,
        fetchedFor: fetchedFor,
        payload: {
          bookings: bookings.reverse(),
          active: active,
          tracked: tracked
        },
      });
      // (A second FETCH_BOOKINGS_SUCCESS with a null payload used to follow
      // whenever a trip was tracked; the reducer spreads the payload, so it
      // changed no field and only re-rendered every bookinglistdata subscriber.)
    } else {
      dispatch({
        type: FETCH_BOOKINGS_FAILED,
        fetchedFor: fetchedFor,
        payload: store.getState().languagedata.defaultLanguage.no_bookings,
      });
    }
  };

  if (isAdminScope) {
    // Tail listener for live new-booking arrivals. Replaces the original
    // continuous onValue (which re-downloaded the entire /bookings tree
    // on every write — the original cost driver). This one is scoped to
    // bookings created in the last 30s (and onwards) via tripdate index,
    // so each new booking from a mobile customer/driver fires a single
    // small event. No flood on attach because startAt is recent.
    //
    // Without this, an admin sees a frozen snapshot until cache TTL
    // (10 min) — the gap the user reported as "1 min before showing up".
    // Maps a raw RTDB booking record into the shape the table renders.
    // Extracted so both onChildAdded and onChildChanged use identical logic.
    const shapeBookingForTable = (id, raw) => ({
      ...raw,
      id,
      pickupAddress: raw.pickup ? raw.pickup.add : '',
      dropAddress: raw.drop ? raw.drop.add : '',
      discount: raw.discount || 0,
      cashPaymentAmount: raw.cashPaymentAmount || 0,
      cardPaymentAmount: raw.cardPaymentAmount || 0,
      bookingDate: raw.tripdate || raw.bookingDate,
    });

    const attachAdminTailListener = () => {
      if (_adminTailUnsub) {
        try { _adminTailUnsub(); } catch (_) { /* ignore */ }
        _adminTailUnsub = null;
      }
      if (_adminTailChangeUnsub) {
        try { _adminTailChangeUnsub(); } catch (_) { /* ignore */ }
        _adminTailChangeUnsub = null;
      }

      // Sentinel for the "is this booking truly new?" check below. Captured
      // here so each call to attachAdminTailListener has its own cutoff.
      const sessionStart = Date.now();

      // Two separate queries because the two events have different needs:
      //   • onChildAdded uses a narrow 5-min window — RTDB will fire it
      //     for every currently-matching child on attach, so a tight
      //     window minimizes the noise the handler has to filter out.
      //     The handler's sessionStart guard is the actual filter.
      //   • onChildChanged uses a 24-hour window so status flips on
      //     bookings up to a day old still surface live (the bulk of
      //     booking lifecycle churn happens within 24h of creation).
      const newBookingsQuery = useMs
        ? bookingListByMsRef(sessionStart - 5 * 60 * 1000)
        : bookingListRef(userInfo.uid, userInfo.usertype, sessionStart - 5 * 60 * 1000);
      const recentBookingsQuery = useMs
        ? bookingListByMsRef(sessionStart - 24 * 60 * 60 * 1000)
        : bookingListRef(userInfo.uid, userInfo.usertype, sessionStart - 24 * 60 * 60 * 1000);

      // NEW bookings — fires once per child created after listener attaches.
      _adminTailUnsub = onChildAdded(newBookingsQuery, (snap) => {
        const id = snap.key;
        const raw = snap.val();
        if (!id || !raw) return;

        // Skip any booking that already existed before this listener
        // attached. Those are covered by the initial cache hit / get();
        // letting them through here would duplicate-prepend them and
        // overwrite live data with the snapshot at attach time.
        const tripdateMs = raw.tripdate ? new Date(raw.tripdate).getTime() : 0;
        if (!tripdateMs || tripdateMs < sessionStart) return;

        const state = store.getState().bookinglistdata;
        const currentBookings = state.bookings || [];
        // Belt-and-suspenders dedupe in case of races.
        if (currentBookings.some(b => b.id === id)) return;

        const booking = shapeBookingForTable(id, raw);

        // Prepend — the table sorts desc by bookingDate, so newest first.
        const newBookings = [booking, ...currentBookings];
        const active = newBookings.filter(b => ACTIVE_BOOKING_STATUSES.includes(b.status));
        const payload = { bookings: newBookings, active, tracked: state.tracked || null };

        dispatch({ type: FETCH_BOOKINGS_SUCCESS, fetchedFor: fetchedFor, payload });
        bookingsCache.write(defaultCacheKey, payload, 'bookingsCache');
        console.debug('[bookings] live: new booking arrived', id, raw.status);
      });

      // STATUS FLIPS on existing bookings — fires when any booking already
      // in the recent window is mutated (status change, wallet settlement,
      // tracking update, etc.). Without this, the BookingHistory table
      // froze at admin's last load: a driver flipping ACCEPTED → STARTED
      // → COMPLETE on mobile wasn't visible until reload or TTL expiry.
      // Each event is small (~5 KB for that one booking).
      _adminTailChangeUnsub = onChildChanged(recentBookingsQuery, (snap) => {
        const id = snap.key;
        const raw = snap.val();
        if (!id || !raw) return;

        const state = store.getState().bookinglistdata;
        const currentBookings = state.bookings || [];
        const idx = currentBookings.findIndex(b => b.id === id);
        if (idx < 0) return; // not in current view, ignore

        const updated = shapeBookingForTable(id, raw);
        const newBookings = [...currentBookings];
        newBookings[idx] = updated;
        const active = newBookings.filter(b => ACTIVE_BOOKING_STATUSES.includes(b.status));
        const payload = { bookings: newBookings, active, tracked: state.tracked || null };

        dispatch({ type: FETCH_BOOKINGS_SUCCESS, fetchedFor: fetchedFor, payload });
        bookingsCache.write(defaultCacheKey, payload, 'bookingsCache');
        console.debug('[bookings] live: status changed', id, raw.status);
      });
    };

    // Only cache the default 30-day window. Custom date ranges (admin
    // explicitly picks a custom window via UI) skip the cache and hit
    // the network each time — they're infrequent and the cache key
    // would proliferate.
    const isDefaultRange = !dateRange;
    const cacheKey = isDefaultRange ? defaultCacheKey : null;
    const readPromise = cacheKey ? bookingsCache.read(cacheKey, 'bookingsCache') : Promise.resolve(null);
    readPromise.then((cachedSnap) => {
      if (cachedSnap) {
        // The cache stores the post-processed dispatch payload directly,
        // not the raw snapshot — saves re-running the (potentially heavy)
        // map+filter loop on cache hit.
        dispatch({
          type: FETCH_BOOKINGS_SUCCESS,
        fetchedFor: fetchedFor,
          payload: cachedSnap,
        });
        // Attach the live tail listener so new bookings created on
        // mobile after this snapshot also stream into the table.
        attachAdminTailListener();
        return;
      }
      get(baseRef).then((snapshot) => {
        // Re-run processSnapshot logic but capture the dispatched payload
        // so we can write it to cache. processSnapshot dispatches directly,
        // so we re-do its work here for the admin-with-cache path.
        if (snapshot.val()) {
          const data = snapshot.val();
          const active = [];
          const bookings = Object.keys(data)
            .map((i) => {
              data[i].id = i;
              data[i].pickupAddress = data[i].pickup.add;
              data[i].dropAddress = data[i].drop.add;
              data[i].discount = data[i].discount || 0;
              data[i].cashPaymentAmount = data[i].cashPaymentAmount || 0;
              data[i].cardPaymentAmount = data[i].cardPaymentAmount || 0;
              data[i].bookingDate = data[i].tripdate || data[i].bookingDate;
              const bookingDateStr = data[i].tripdate || data[i].bookingDate;
              if (bookingDateStr) {
                const bookingDate = new Date(bookingDateStr);
                const fromDateObj = new Date(fromDate);
                const toDateObj = new Date(toDate);
                const fiveMinutesAgo = new Date();
                fiveMinutesAgo.setMinutes(fiveMinutesAgo.getMinutes() - 5);
                const isRecentBooking = bookingDate >= fiveMinutesAgo;
                if (!isRecentBooking && (bookingDate < fromDateObj || bookingDate > toDateObj)) {
                  return null;
                }
              }
              return data[i];
            }).filter(b => b !== null);
          for (let i = 0; i < bookings.length; i++) {
            // Use the shared ACTIVE_BOOKING_STATUSES constant — the previous
            // hardcoded duplicate here is what made donkey-meter
            // PENDING_DRIVER_APPROVAL invisible on the customer/driver
            // onValue path (MyBooking tab badge stayed at 0, RideList didn't
            // render the "Go to booking" CTA on the pending row).
            if (ACTIVE_BOOKING_STATUSES.indexOf(bookings[i].status) != -1) {
              active.push(bookings[i]);
            }
          }
          const payload = { bookings: bookings.reverse(), active, tracked: null };
          if (cacheKey) {
            bookingsCache.write(cacheKey, payload, 'bookingsCache');
          }
          dispatch({ type: FETCH_BOOKINGS_SUCCESS, fetchedFor: fetchedFor, payload });
          // Fresh fetch path also needs the live listener for new bookings
          // arriving after this snapshot.
          attachAdminTailListener();
        } else {
          dispatch({
            type: FETCH_BOOKINGS_FAILED,
        fetchedFor: fetchedFor,
            payload: store.getState().languagedata.defaultLanguage.no_bookings,
          });
          // Even with no current bookings, attach the listener — the very
          // next mobile booking will stream in live instead of waiting
          // for TTL/refresh.
          attachAdminTailListener();
        }
      }).catch((err) => {
        dispatch({
          type: FETCH_BOOKINGS_FAILED,
        fetchedFor: fetchedFor,
          payload: err.message || 'Failed to fetch bookings',
        });
      });
    });
  } else {
    if (_mobileBookingsQuery) {
      try { off(_mobileBookingsQuery); } catch (e) { /* already detached */ }
    }
    off(baseRef);
    onValue(baseRef, processSnapshot);
    _mobileBookingsQuery = baseRef;
  }
};

export const updateBooking = (booking) => async (dispatch) => {

  const {
    auth,
    trackingRef,
    singleBookingRef,
    singleUserRef,
    walletHistoryRef,
    settingsRef,
    userRatingsRef
  } = firebase;

  dispatch({
    type: UPDATE_BOOKING,
    payload: booking,
  });

  // Surgically patch the booking in the cache instead of invalidating —
  // admins see their own status change on next reload without triggering
  // a full /bookings refetch. Driver-record patches (queue, rating)
  // happen further below where each status branch knows what changed.
  patchBookingInCache(booking.id, booking);

  const settingsdata = await get(settingsRef);
  const settings = settingsdata.val();
  
  if (booking.status == 'PAYMENT_PENDING') {
    update(singleBookingRef(booking.id),booking);
  }
  if (booking.status == 'NEW' || booking.status == 'ACCEPTED') {
    update(singleBookingRef(booking.id), updateDriverQueue(booking));
  }
  if (booking.status == 'ARRIVED') {
    let dt = new Date();
    booking.driver_arrive_time = dt.getTime().toString();
    update(singleBookingRef(booking.id),booking);
    if(booking.customer_token){
      RequestPushMsg(
        booking.customer_token,
        {
            title: store.getState().languagedata.defaultLanguage.notification_title,
            msg: store.getState().languagedata.defaultLanguage.driver_near,
            screen: 'BookedCab',
            params: { bookingId: booking.id }
        });
    }
  }
  
  if (booking.status == 'STARTED') {
    let dt = new Date();
    let localString = dt.getHours() + ":" + dt.getMinutes() + ":" + dt.getSeconds();
    let timeString = dt.getTime();
    booking.trip_start_time = localString;
    booking.startTime = timeString;
    update(singleBookingRef(booking.id),booking);

    const driverLocation = store.getState().gpsdata.location;
    
    push(trackingRef(booking.id),{
      at: new Date().getTime(),
      status: 'STARTED',
      lat: driverLocation.lat,
      lng: driverLocation.lng
    });

    if(booking.customer_token){
      RequestPushMsg(
        booking.customer_token,
        {
            title: store.getState().languagedata.defaultLanguage.notification_title,
            msg: store.getState().languagedata.defaultLanguage.driver_journey_msg + booking.reference,
            screen: 'BookedCab',
            params: { bookingId: booking.id }
        });
      }
   }

  if (booking.status == 'REACHED') {

    const driverLocation = store.getState().gpsdata.location ? store.getState().gpsdata.location : booking.drop;
    push(trackingRef(booking.id),{
      at: new Date().getTime(),
      status: 'REACHED',
      lat: driverLocation.lat,
      lng: driverLocation.lng
    });

    let address = await fetchTripEndAddress(booking, driverLocation, settings);

    let bookingObj = await addActualsToBooking(booking, address, driverLocation);
    update(singleBookingRef(booking.id), bookingObj);

    if(booking.customer_token){
      RequestPushMsg(
        booking.customer_token,
        {
            title: store.getState().languagedata.defaultLanguage.notification_title,
            msg: store.getState().languagedata.defaultLanguage.driver_completed_ride,
            screen: 'BookedCab',
            params: { bookingId: booking.id }
        });
    }
  }

  if (booking.status == 'PENDING') {
    update(singleBookingRef(booking.id),booking);
    update(singleUserRef(booking.driver), { queue: false });
    if (booking.driver) patchUsersCacheUpsert(booking.driver, { queue: false });
  }
  if (booking.status == 'PAID') {
    if(booking.booking_from_web){
      booking.status = 'COMPLETE';
    }
    if(booking.streetPickup && booking.payment_mode !== 'wallet'){
      booking.status = 'COMPLETE';
    }
    update(singleBookingRef(booking.id), booking);
    // Re-patch the cache with the (possibly status-rewritten) booking so
    // the cached entry reflects whatever final status was written above.
    patchBookingInCache(booking.id, booking);
    if(booking.driver == auth.currentUser.uid && (booking.prepaid || booking.payment_mode == 'cash' || booking.payment_mode == 'wallet')){
      update(singleUserRef(booking.driver), { queue: false });
      if (booking.driver) patchUsersCacheUpsert(booking.driver, { queue: false });
    }
    // Cloud Functions credit driver wallet, debit topup, push earnings,
    // and (for wallet payments) debit customer wallet — all asynchronously
    // after this action returns. Re-read both records so the admin cache
    // reflects the post-settlement balances.
    refreshUsersAfterSettlement([booking.driver, booking.customer]);

    if(booking.customer_token){
      RequestPushMsg(
        booking.customer_token,
        {
            title: store.getState().languagedata.defaultLanguage.notification_title,
            msg: store.getState().languagedata.defaultLanguage.success_payment,
            screen: 'BookedCab',
            params: { bookingId: booking.id }
        });
    }

    if(booking.driver_token){
        RequestPushMsg(
          booking.driver_token,
          {
              title: store.getState().languagedata.defaultLanguage.notification_title,
              msg: store.getState().languagedata.defaultLanguage.success_payment,
              screen: 'BookedCab',
              params: { bookingId: booking.id }
          });
      }
    }

  if (booking.status == 'COMPLETE') {
    update(singleBookingRef(booking.id), booking);
    // Same settlement coverage as PAID — COMPLETE may also trigger wallet
    // adjustments depending on payment_mode and prior status. Cheap insurance.
    refreshUsersAfterSettlement([booking.driver, booking.customer]);
    if (booking.rating) {
      if(booking.driver_token){
        RequestPushMsg(
          booking.driver_token,
          {
              title: store.getState().languagedata.defaultLanguage.notification_title,
              msg:  store.getState().languagedata.defaultLanguage.received_rating.toString().replace("X", booking.rating.toString()),
              screen: 'BookedCab',
              params: { bookingId: booking.id }
          });
      }
      const ratingsSnap = await get(userRatingsRef(booking?.driver));
      let ratings = ratingsSnap.val();
      let rating;
      if(ratings){
        let sum = 0;
        const arr = Object.values(ratings);
        for (let i = 0; i< arr.length ; i++){
          sum = sum + arr[i].rate
        }
        sum = sum + booking.rating;
        rating = parseFloat(sum / (arr.length + 1)).toFixed(1);
      }else{
        rating =  booking.rating;
      }
      update(singleUserRef(booking.driver),{rating: rating});
      if (booking.driver) patchUsersCacheUpsert(booking.driver, { rating: rating });
      push(userRatingsRef(booking.driver), {
        user: booking?.customer,
        rate: booking?.rating,
        bookingId: booking?.id
      });
    }
  }
};

export const cancelBooking = (data) => (dispatch) => {
  const {
    singleBookingRef,
    singleUserRef,
    requestedDriversRef
  } = firebase;

  dispatch({
    type: CANCEL_BOOKING,
    payload: data,
  });

  // Surgical patch: flip the cached booking to CANCELLED. The patch
  // helper auto-removes it from the active list on the recompute pass.
  patchBookingInCache(data.booking.id, {
    status: 'CANCELLED',
    reason: data.reason,
    cancelledBy: data.cancelledBy,
  });

  // Cancellation can trigger a fee debit (customer wallet) and credit
  // (driver wallet) via Cloud Function. Refresh both parties so the cache
  // doesn't show stale balances.
  refreshUsersAfterSettlement([data.booking.driver, data.booking.customer]);

  update(singleBookingRef(data.booking.id),{
    status: 'CANCELLED',
    reason: data.reason,
    cancelledBy: data.cancelledBy
  }).then(async () => {
    // Free the assigned driver on ANY cancellation — by customer or
    // driver, at any pre-completion status. Previously only
    // NEW/ACCEPTED/ARRIVED were handled, so cancelling a STARTED trip
    // left the driver stuck with queue=true (busy): they stopped
    // receiving new requests until the periodic rebuildActiveDriversSlim
    // sweep eventually reconciled it. /active_drivers_slim projects this
    // same queue flag on its 2-min rebuild, so clearing it on the user
    // record is what frees the driver for dispatch everywhere.
    if (data.booking.driver) {
      update(singleUserRef(data.booking.driver),{ queue: false });
      patchUsersCacheUpsert(data.booking.driver, { queue: false });
      if(data.booking.driver_token){
        RequestPushMsg(
          data.booking.driver_token,
          {
              title: store.getState().languagedata.defaultLanguage.notification_title,
              msg:  store.getState().languagedata.defaultLanguage.booking_cancelled + data.booking.id,
              screen: 'BookedCab',
              params: { bookingId: data.booking.id }
          });
        }

        if(data.booking.customer_token){
          RequestPushMsg(
            data.booking.customer_token,
            {
                title: store.getState().languagedata.defaultLanguage.notification_title,
                msg:  store.getState().languagedata.defaultLanguage.booking_cancelled + data.booking.id,
                screen: 'BookedCab',
                params: { bookingId: data.booking.id }
            });
        }
   }
    if (data.booking.status === 'NEW') {
      remove(requestedDriversRef(data.booking.id));
    }

    // The customer cancelled it themselves: they know, and the SMS package is
    // not for telling them (owner, 1 Nov 2026). A cancel by a driver or by the
    // office still tells them.
    if (data.cancelledBy === 'customer') return;

    // Send SMS for trip cancellation
    const {config} = firebase;
    const settings = store.getState().settingsdata.settings;
    const host = (typeof window !== 'undefined' && window.location && settings.CompanyWebsite === window.location.origin)
        ? window.location.origin
        : `https://${config.projectId}.web.app`;

    const url = `${host}/send_booking_cancelled_sms`;

    try {
      await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Basic ' + base64.encode(config.projectId + ':' + AccessKey)
        },
        body: JSON.stringify({
          customer_contact: data.booking.customer_contact,
          reference: data.booking.reference,
          reason: data.reason || 'Not specified',
        })
      });
    } catch (err) {
      console.log('[TripCancelledSMS] Error sending SMS:', err.message);
    }

  });
};

export const updateBookingImage = (booking, imageType, imageBlob) => (dispatch) => {
  const   {
    singleBookingRef,
    bookingImageRef
  } = firebase;
  uploadBytesResumable(bookingImageRef(booking.id,imageType), imageBlob).then(() => {
    imageBlob.close()
    return getDownloadURL(bookingImageRef(booking.id,imageType))
  }).then((url) => {
    if(imageType == 'pickup_image'){
      booking.pickup_image = url;
    }
    if(imageType == 'deliver_image'){
      booking.deliver_image = url;
    }
    update(singleBookingRef(booking.id), booking);
    dispatch({
      type: UPDATE_BOOKING,
      payload: booking,
    });
  })
};

export const forceEndBooking = (booking) => async (dispatch) => {

  const {
    trackingRef,
    singleBookingRef,
    singleUserRef,
    walletHistoryRef,
    settingsRef,
  } = firebase;

  dispatch({
    type: UPDATE_BOOKING,
    payload: booking,
  });
  
  if (booking.status == 'STARTED') {

    push(trackingRef(booking.id),{
      at: new Date().getTime(),
      status: 'REACHED',
      lat: booking.drop.lat,
      lng: booking.drop.lng
    });

    const end_time = new Date();
    const diff = (end_time.getTime() - parseFloat(booking.startTime)) / 1000;
    const totalTimeTaken = Math.abs(Math.round(diff));
    booking.trip_end_time = end_time.getHours() + ":" + end_time.getMinutes() + ":" + end_time.getSeconds();
    booking.endTime = end_time.getTime();
    booking.total_trip_time = totalTimeTaken;

    if(booking.customer_token){
      RequestPushMsg(
        booking.customer_token,
        {
            title: store.getState().languagedata.defaultLanguage.notification_title,
            msg: store.getState().languagedata.defaultLanguage.driver_completed_ride,
            screen: 'BookedCab',
            params: { bookingId: booking.id }
        });
    }

    update(singleUserRef(booking.driver),{ queue: false });
    if (booking.driver) patchUsersCacheUpsert(booking.driver, { queue: false });

    if(booking.prepaid){

      const settingsdata = await get(settingsRef);
      const settings = settingsdata.val();

      const driverSnap = await get(singleUserRef(booking.driver));
      const driverData = driverSnap.val();
      let walletBalance = parseFloat(driverData.walletBalance) || 0;
      let topUpBalance = parseFloat(driverData.topUpBalance) || 0;

      // For prepaid bookings, driver earnings go to walletBalance
      // This is only called for prepaid bookings, not cash
      if(booking.payment_mode !== 'cash') {
        // Only add to wallet for non-cash payments (card, wallet, etc.)
        walletBalance = walletBalance + parseFloat(booking.driver_share);
      }

      // Commission deduction comes from topUpBalance if there's a cash component
      // (This might happen in mixed payment scenarios)
      // Flat-daily drivers pay no per-trip commission. The booking stamps
      // (client flat_rate_driver / server commission_mode) cover trips written
      // by an updated app or already settled server-side; the profile fallback
      // only applies when the caller IS the booking's driver — an admin
      // force-ending someone else's trip must not be tested against the
      // admin's own profile.
      const callerProfile = store.getState().auth.profile;
      const flatRateDriver = booking.flat_rate_driver === true
        || booking.commission_mode === 'flat_daily'
        || (callerProfile && callerProfile.uid === booking.driver
            && isFlatRateDriver(callerProfile, settings, store.getState().cartypes.cars));
      if(parseFloat(booking.cashPaymentAmount)>0 && !flatRateDriver){
        topUpBalance = topUpBalance - parseFloat(booking.convenience_fees);
      }

      // Update both balances
      const balancePatch = {
        walletBalance: parseFloat(walletBalance.toFixed(settings.decimal)),
        topUpBalance: parseFloat(topUpBalance.toFixed(settings.decimal)),
      };
      update(singleUserRef(booking.driver), balancePatch);
      if (booking.driver) patchUsersCacheUpsert(booking.driver, balancePatch);

      let details = {
        type: 'Credit',
        amount: parseFloat(booking.driver_share).toFixed(settings.decimal),
        date: new Date().getTime(),
        txRef: booking.id
      }
      push(walletHistoryRef(booking.driver),details);

      if(parseFloat(booking.cashPaymentAmount)>0){
        let details = {
          type: 'Debit',
          amount: booking.cashPaymentAmount,
          date: new Date().getTime(),
          txRef: booking.id
        }
        push(walletHistoryRef(booking.driver), details);
      }

      if(booking.customer_token){
        RequestPushMsg(
          booking.customer_token,
          {
              title: store.getState().languagedata.defaultLanguage.notification_title,
              msg: store.getState().languagedata.defaultLanguage.success_payment,
              screen: 'BookedCab',
              params: { bookingId: booking.id }
          });
      }

      if(booking.driver_token){
        RequestPushMsg(
          booking.driver_token,
          {
              title: store.getState().languagedata.defaultLanguage.notification_title,
              msg: store.getState().languagedata.defaultLanguage.success_payment,
              screen: 'BookedCab',
              params: { bookingId: booking.id }
          });
        }
      booking.status = 'PAID';
    } else{
      booking.status = 'REACHED';
    }

    update(singleBookingRef(booking.id), booking);
    patchBookingInCache(booking.id, booking);
    // Same as updateBooking PAID — Cloud Functions may settle wallets
    // after this returns. Refresh both parties so the cache catches up.
    refreshUsersAfterSettlement([booking.driver, booking.customer]);

    // Send SMS for trip forced completion
    const {config} = firebase;
    const settings = store.getState().settingsdata.settings;
    const host = (typeof window !== 'undefined' && window.location && settings.CompanyWebsite === window.location.origin)
        ? window.location.origin
        : `https://${config.projectId}.web.app`;

    const url = `${host}/send_booking_completed_sms`;

    try {
      await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Basic ' + base64.encode(config.projectId + ':' + AccessKey)
        },
        body: JSON.stringify({
          bookingId: booking.id,
          driver_name: booking.driver_name,
          driver_contact: booking.driver_contact,
          customer_contact: booking.customer_contact,
          vehicle_number: booking.vehicle_number,
          reference: booking.reference,
          trip_end_time: booking.trip_end_time,
          // Send the discounted/actual paid amount, not the headline
          // trip_cost. Fields can be either numbers OR .toFixed() strings
          // depending on the write path, so we coerce + compare instead of
          // relying on a bare `||` chain (string "0" is truthy and would
          // short-circuit incorrectly). Priority:
          //   1. customer_paid — set during payment flow, authoritative
          //   2. payableAmount — set at booking time = trip_cost - discount
          //   3. trip_cost     — fallback for legacy bookings
          trip_cost: (() => {
            const paid = parseFloat(booking.customer_paid) || 0;
            const payable = parseFloat(booking.payableAmount) || 0;
            const cost = parseFloat(booking.trip_cost) || 0;
            return paid > 0 ? paid : (payable > 0 ? payable : cost);
          })(),
        })
      });
    } catch (err) {
      console.log('[TripCancelledSMS] Error sending SMS:', err.message);
    }
  }
};